import Dialog from "@mui/material/Dialog";
import { useEmployeePerGrantOffers } from "../../queries/optionBuyback";
import { ButtonSecondary, HStack, VStack } from "../../components/utils";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import StatusLabelColors from "../../constants/StatusLabelColors";

interface Props {
  eventId: string;
  employeeId: string;
  employeeName: string;
  onClose: () => void;
}

export function GrantDetailsDialog({
  eventId,
  employeeId,
  employeeName,
  onClose,
}: Props) {
  const currency = getCurrencyType();
  const { data: unsortedGrants = [], isLoading } = useEmployeePerGrantOffers(
    eventId,
    employeeId
  );

  const grants = [...unsortedGrants].sort((a, b) =>
    (a.liquidityGroupType || "Other").localeCompare(
      b.liquidityGroupType || "Other"
    )
  );

  const hasFinancials = grants.some(
    (g) => g.totalCashout != null || g.netPayout != null
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <VStack className="p-6 gap-4 min-w-[600px]">
        <HStack className="justify-between items-center">
          <div>
            <p className="text-lg font-semibold text-gray-dark">
              Grant Details
            </p>
            <p className="text-sm text-gray-500">{employeeName}</p>
          </div>
        </HStack>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">
            Loading...
          </div>
        ) : grants.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No grant offers found for this employee.
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Grant ID
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Grant Type
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Liquidity Group
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Plan
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Granted
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Vested
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Eligibility
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Willingness
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Price/Option
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                    Strike Price
                  </th>
                  {hasFinancials && (
                    <>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                        Total Cashout
                      </th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">
                        Net Payout
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {grants.map((grant, idx) => {
                  const stateKey =
                    grant.offerState as keyof typeof StatusLabelColors;
                  const stateStyle = StatusLabelColors[stateKey] ?? {
                    color: "#555",
                    backgroundColor: "#eee",
                  };
                  return (
                    <tr
                      key={idx}
                      className="border-b border-dashed border-gray-100 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 font-medium text-[#464E5F] whitespace-nowrap">
                        {grant.grantIdentifier}
                      </td>
                      <td className="px-3 py-2 text-[#464E5F] whitespace-nowrap">
                        {grant.grantType}
                      </td>
                      <td className="px-3 py-2 text-[#464E5F] whitespace-nowrap">
                        {grant.liquidityGroupType || "Other"}
                      </td>
                      <td className="px-3 py-2 text-[#464E5F] whitespace-nowrap">
                        {grant.planName}
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F]">
                        {grant.grantedOptions.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F]">
                        {grant.vestedOptions.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F]">
                        {grant.eligibility.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F]">
                        {grant.willingness.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={stateStyle}
                        >
                          {grant.offerState}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F] whitespace-nowrap">
                        {formatCurrency(grant.pricePerOption, currency)}
                      </td>
                      <td className="px-3 py-2 text-right text-[#464E5F] whitespace-nowrap">
                        {formatCurrency(grant.strikePrice, currency)}
                      </td>
                      {hasFinancials && (
                        <>
                          <td className="px-3 py-2 text-right text-[#464E5F] whitespace-nowrap">
                            {grant.totalCashout != null
                              ? formatCurrency(grant.totalCashout, currency)
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-[#464E5F] whitespace-nowrap">
                            {grant.netPayout != null
                              ? formatCurrency(grant.netPayout, currency)
                              : "—"}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <HStack className="justify-end pt-2">
          <ButtonSecondary onClick={onClose}>Close</ButtonSecondary>
        </HStack>
      </VStack>
    </Dialog>
  );
}
